import { useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  Line, LineChart, CartesianGrid,
} from "recharts";
import { Button } from "@/components/ui/button";
import { MACROS } from "@/lib/api";

const fmt = (d) =>
  new Date(`${d}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });

const ThemedTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-[#D5CEBD] bg-sand px-3 py-2 shadow-md">
      <p className="font-heading text-xs font-semibold text-deep-olive">{fmt(label)}</p>
      {payload.map((p) => (
        <p key={p.name} className="text-xs text-stone-grey">
          <span style={{ color: p.color }}>■</span> {p.name}: {Math.round(p.value)}
        </p>
      ))}
    </div>
  );
};

export const TrendsChart = ({ trends, days, onDaysChange }) => {
  const [view, setView] = useState("calories");
  if (!trends) return null;

  return (
    <div
      className="bg-surface rounded-xl border border-[#E8E3D9] p-6 hover:shadow-md transition-shadow duration-200"
      data-testid="trends-card"
    >
      <div className="flex flex-wrap items-start gap-4">
        <div className="mr-auto">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-moss">
            {days === 7 ? "Last 7 days" : "Last 30 days"}
          </p>
          <h3 className="mt-1 font-heading text-xl sm:text-2xl font-medium tracking-tight text-deep-olive">
            {view === "calories" ? "Calorie intake" : "Macro breakdown"}
          </h3>
        </div>
        <div className="flex gap-1.5">
          {[7, 30].map((d) => (
            <Button
              key={d}
              data-testid={`range-${d}-button`}
              onClick={() => onDaysChange(d)}
              className={`h-9 rounded-full px-4 text-sm transition-colors duration-200 ${
                days === d
                  ? "bg-forest text-sand hover:bg-forest-hover"
                  : "bg-transparent text-stone-grey hover:bg-surface-hover hover:text-deep-olive"
              }`}
            >
              {d}d
            </Button>
          ))}
          {["calories", "macros"].map((v) => (
            <Button
              key={v}
              data-testid={`view-${v}-button`}
              onClick={() => setView(v)}
              className={`h-9 rounded-full px-4 text-sm capitalize transition-colors duration-200 ${
                view === v
                  ? "bg-deep-olive text-sand hover:bg-forest"
                  : "bg-transparent text-stone-grey hover:bg-surface-hover hover:text-deep-olive"
              }`}
            >
              {v}
            </Button>
          ))}
        </div>
      </div>

      <div className="mt-6 h-[260px]" data-testid="trends-chart">
        <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={240}>
          {view === "calories" ? (
            <BarChart data={trends.series} margin={{ left: -18, right: 4 }}>
              <XAxis
                dataKey="date"
                tickFormatter={fmt}
                tick={{ fill: "#6B7262", fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                interval="preserveStartEnd"
              />
              <YAxis tick={{ fill: "#6B7262", fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip content={<ThemedTooltip />} cursor={{ fill: "#EAE5D9" }} />
              <Bar dataKey="calories" name="Eaten" fill="#2D4A22" radius={[5, 5, 0, 0]} />
              <Bar dataKey="served" name="Served" fill="#D5CEBD" radius={[5, 5, 0, 0]} />
            </BarChart>
          ) : (
            <LineChart data={trends.series} margin={{ left: -18, right: 4 }}>
              <CartesianGrid stroke="#E8E3D9" vertical={false} />
              <XAxis
                dataKey="date"
                tickFormatter={fmt}
                tick={{ fill: "#6B7262", fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                interval="preserveStartEnd"
              />
              <YAxis tick={{ fill: "#6B7262", fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip content={<ThemedTooltip />} />
              {MACROS.map((m) => (
                <Line
                  key={m.key}
                  type="monotone"
                  dataKey={m.key}
                  name={m.label}
                  stroke={m.color}
                  strokeWidth={2.5}
                  dot={false}
                />
              ))}
            </LineChart>
          )}
        </ResponsiveContainer>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-4 border-t border-[#E8E3D9] pt-5 sm:grid-cols-4">
        {[
          ["Avg kcal", Math.round(trends.averages.calories)],
          ["Avg protein", `${Math.round(trends.averages.protein)}g`],
          ["Days logged", `${trends.logged_days}/${trends.days}`],
          ["Waste", `${trends.waste_pct}%`],
        ].map(([label, value]) => (
          <div key={label}>
            <p className="text-xs text-stone-grey">{label}</p>
            <p className="font-heading text-lg font-medium text-deep-olive">{value}</p>
          </div>
        ))}
      </div>
    </div>
  );
};
